import {
  LocalStorageService,
  addActionBarItem,
  getAppConfig,
  getServerLocation,
} from '@vendure/admin-ui/core';

const OrderExportHelpers = (() => {
  /**
   * Generate a filename for the order export with the current date
   */
  const getOrderExportFilename = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `order-export-${year}-${month}-${day}.csv`;
  };

  /**
   * Get the necessary headers for authentication
   */
  const getFetchHeaders = (
    localStorageService: LocalStorageService,
  ): Record<string, string> => {
    const { tokenMethod, channelTokenKey } = getAppConfig();
    const headers: Record<string, string> = {};

    // Add channel token if available
    const channelToken = localStorageService.get('activeChannelToken');
    if (channelToken) {
      headers[channelTokenKey ?? 'vendure-token'] = channelToken;
    }

    // Add auth token if using bearer authentication
    if (tokenMethod === 'bearer') {
      const authToken = localStorageService.get('authToken');
      if (authToken) {
        headers.authorization = `Bearer ${authToken}`;
      }
    }

    return headers;
  };

  // Return only the functions we want to expose
  return {
    getOrderExportFilename,
    getFetchHeaders,
  };
})();

export default [
  addActionBarItem({
    id: 'order-export',
    locationId: 'order-list',
    label: 'Export Orders',
    icon: 'export',
    requiresPermission: 'ReadOrder',
    onClick: async (event, context) => {
      try {
        const { injector } = context;
        const localStorageService = injector.get(LocalStorageService);

        const serverLocation = getServerLocation();
        const csvEndpoint = `${serverLocation}/data-export/orders`;

        const headers = OrderExportHelpers.getFetchHeaders(localStorageService);

        context.notificationService.info('Preparing order export...');

        fetch(csvEndpoint, {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(context.route.snapshot.queryParams),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Export failed: ${response.status} ${response.statusText}`,
              );
            }
            return response.blob();
          })
          .then((blob) => {
            // Create and trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = OrderExportHelpers.getOrderExportFilename();
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            context.notificationService.success('Order export completed');
          })
          .catch((error) => {
            console.error('Export error:', error);
            context.notificationService.error(
              `Export failed: ${error.message}`,
            );
          });
      } catch (error) {
        context.notificationService.error(
          'Error preparing export: ' + (error as Error)?.message,
        );
      }
    },
  }),
];
